import { Children, createContext, isValidElement, useContext, useMemo } from "react";
import type { Element, Nodes } from "hast";
import ReactMarkdown, { type Components, type ExtraProps } from "react-markdown";
import remarkDirective from "remark-directive";
import remarkGfm from "remark-gfm";
import { BillingRatesSection } from "./BillingRatesSection";
import { DocLink } from "./DocLink";
import {
	DocAudience,
	DocCode,
	DocCommand,
	DocExamplePrompts,
	DocHeading,
	DocList,
	DocNextLink,
	DocParagraph,
	DocSectionLink,
	DocSubheading,
	DocTable,
	DocTableCell,
	DocTableHead,
	DocTableHeaderCell,
	DocTabs,
	DocTerm,
} from "./DocElements";
import { remarkDocSyntax } from "./remark-doc-syntax";

const REMARK_PLUGINS = [remarkGfm, remarkDirective, remarkDocSyntax];

/** ページを表示しているか。表示したときに取り直す部品 (料金ページの単価) が見る。 */
const DocActiveContext = createContext(false);

type DataProps = { "data-doc"?: string; [name: `data-${string}`]: string | undefined };

function hastText(node: Nodes): string {
	if (node.type === "text") return node.value;
	return "children" in node ? node.children.map(hastText).join("") : "";
}

function childElements(node: Element | undefined, tagName: string): Element[] {
	return (node?.children ?? []).filter(
		(child): child is Element => child.type === "element" && child.tagName === tagName,
	);
}

type BlockProps = DataProps & { children?: React.ReactNode };

/** 中に書いたディレクティブ (タブ・カテゴリ) の要素。間の改行の文字列は除く。 */
function childBlocks(children: React.ReactNode) {
	return Children.toArray(children).filter(isValidElement<BlockProps>);
}

function DocBlock({ node, children, ...rest }: ExtraProps & { children?: React.ReactNode }) {
	const active = useContext(DocActiveContext);
	const props = rest as DataProps;
	switch (props["data-doc"]) {
		case "tabs":
			return (
				<DocTabs
					framed={props["data-framed"] !== undefined}
					tabs={childBlocks(children).map((tab) => ({
						key: tab.props["data-key"] ?? "",
						label: tab.props["data-label"] ?? "",
						content: tab.props.children,
					}))}
				/>
			);
		case "examples":
			return (
				<DocExamplePrompts
					prompts={childElements(node, "ul")
						.flatMap((list) => childElements(list, "li"))
						.map((item) => hastText(item).trim())}
				/>
			);
		case "audience":
			return <DocAudience>{children}</DocAudience>;
		case "next":
			return <DocNextLink to={props["data-to"] ?? ""}>{children}</DocNextLink>;
		case "billing-rates":
			return (
				<BillingRatesSection
					active={active}
					descriptions={Object.fromEntries(
						childBlocks(children).map((category) => [
							category.props["data-id"],
							category.props.children,
						]),
					)}
				/>
			);
		default:
			throw new Error(`利用ガイドの Markdown に描けない要素があります: ${props["data-doc"]}`);
	}
}

function unsupported(syntax: string) {
	return () => {
		throw new Error(`利用ガイドでは${syntax}を使えません`);
	};
}

const COMPONENTS: Components = {
	h1: unsupported("見出し 1 (# …)"),
	h4: unsupported("見出し 4 (#### …)"),
	h5: unsupported("見出し 5 (##### …)"),
	h6: unsupported("見出し 6 (###### …)"),
	em: unsupported("斜体 (*…*)"),
	del: unsupported("取り消し線 (~~…~~)"),
	blockquote: unsupported("引用 (> …)"),
	hr: unsupported("区切り線 (---)"),
	img: unsupported("画像 (![…](…))"),
	h2: ({ id, children }) => <DocHeading id={id}>{children}</DocHeading>,
	h3: ({ id, children }) => <DocSubheading id={id}>{children}</DocSubheading>,
	p: ({ children }) => <DocParagraph>{children}</DocParagraph>,
	ul: ({ children }) => <DocList>{children}</DocList>,
	ol: ({ children }) => <DocList ordered>{children}</DocList>,
	strong: ({ children }) => <DocTerm>{children}</DocTerm>,
	code: ({ children }) => <DocCode>{children}</DocCode>,
	pre: ({ node }) => {
		const [code] = childElements(node, "code");
		const className = code?.properties.className;
		if (!Array.isArray(className) || !className.includes("language-command")) {
			throw new Error("利用ガイドのコードブロックは command だけ書けます");
		}
		return <DocCommand command={hastText(code).replace(/\n$/, "")} />;
	},
	a: ({ href = "", children }) =>
		href.startsWith("#") || href.startsWith("/docs/") ? (
			<DocSectionLink to={href}>{children}</DocSectionLink>
		) : (
			<DocLink href={href}>{children}</DocLink>
		),
	table: ({ children }) => <DocTable>{children}</DocTable>,
	thead: ({ children }) => <DocTableHead>{children}</DocTableHead>,
	th: ({ children, ...props }) => (
		<DocTableHeaderCell nowrap={(props as DataProps)["data-nowrap"] !== undefined}>
			{children}
		</DocTableHeaderCell>
	),
	td: ({ children, ...props }) => (
		<DocTableCell nowrap={(props as DataProps)["data-nowrap"] !== undefined}>
			{children}
		</DocTableCell>
	),
	div: DocBlock,
};

/**
 * 利用ガイドの 1 ページの Markdown を、DocElements の部品で描く。書き方は remarkDocSyntax を参照。
 * active が変わっても Markdown は解析し直さず、DocActiveContext を見る部品だけを描き直す。
 */
export function DocMarkdown({ markdown, active }: { markdown: string; active: boolean }) {
	const content = useMemo(
		() => (
			<ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={COMPONENTS}>
				{markdown}
			</ReactMarkdown>
		),
		[markdown],
	);
	return <DocActiveContext value={active}>{content}</DocActiveContext>;
}
